/**
 * Communication step for the Schedule Interview wizard.
 * Dual-pane layout: Candidate (left) | Interviewer (right).
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import moment from 'moment-timezone';
import { formatTimezoneAbbreviationForDisplay } from '@/utils/formatTimezoneAbbreviation';
import { getCachedInterviewerOption } from '../../state/interviewerOptionCache';
import { buildPreviewHtml } from './buildPreviewHtml';
import { Flex } from '@chakra-ui/react';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { useInterviewEmailTemplateOptions } from '../../../InterviewActivity/communication/api';
import { useInterviewActivityCommunicationVariables } from '../../../InterviewActivity/communication/api';
import {
  initialInterviewCommunicationFormState,
  interviewCommunicationReducer,
} from '../../../InterviewActivity/communication/state';
import {
  hydrateCommunicationStateFromPayload,
  toInterviewCommunicationPayload,
} from '../../../InterviewActivity/communication/helpers/payloadMap';
import {
  hasValidationErrors,
  validateInterviewCommunicationForm,
  type CommunicationValidationVariables,
  type InterviewCommunicationValidationErrors,
} from '../../../InterviewActivity/communication/validate';
import {
  getFirstScheduleCommunicationScrollTarget,
  scrollScheduleCommunicationSectionIntoView,
} from './scheduleCommunicationScroll';
import { calendarInviteTemplateNameForApi } from '../../../InterviewActivity/communication/helpers/calendarInviteName';
import { compositionFromTemplateOption } from '../../../InterviewActivity/communication/helpers/templateApply';
import { patchEmailComposition } from '../../../InterviewActivity/communication/helpers/emailUtils';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from '../../../InterviewActivity/communication/types';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  resolveDefaultFromValueForCurrentUser,
  useScheduleSenderOptions,
} from '../../hooks/useScheduleSenderOptions';
import type { ScheduleStepProps } from '../../config/types';
import { isAdhocMode, isSelfScheduleMode } from '../../config/modeClassifiers';
import { CandidatePane } from './CandidatePane';
import { InterviewerPane } from './InterviewerPane';
import type { EmailSentBadgeProps } from '../../editMode/EmailSentBadge';

type SentEmailEntry = {
  type: string;
  recipient_id: number;
  recipient_name: string;
};

function buildSentBadge(
  entries: SentEmailEntry[]
): EmailSentBadgeProps | undefined {
  if (!entries.length) return undefined;

  // Count how many emails each unique recipient received.
  const perRecipient = new Map<number, { name: string; count: number }>();
  for (const e of entries) {
    const existing = perRecipient.get(e.recipient_id);
    if (existing) {
      existing.count += 1;
    } else {
      perRecipient.set(e.recipient_id, { name: e.recipient_name, count: 1 });
    }
  }

  // Group recipient names by their individual email count so the tooltip
  // reads "1 email to: Alice, Bob" (each got 1) or "2 emails to: Alice"
  // (Alice got 2) rather than collapsing unrelated totals.
  const byCount = new Map<number, string[]>();
  Array.from(perRecipient.values()).forEach(({ name, count }) => {
    const names = byCount.get(count) ?? [];
    names.push(name);
    byCount.set(count, names);
  });

  const recipients = Array.from(byCount.entries())
    .sort(([a], [b]) => a - b)
    .map(([count, names]) => ({ count, name: names.join(', ') }));

  return { totalSent: entries.length, recipients };
}

export const CommunicationStep = memo(
  forwardRef<
    StepFormApi,
    ScheduleStepProps & {
      interviewPlan?: Record<string, unknown>;
      candidateName?: string;
      jobTitle?: string;
    }
  >(
    (
      {
        payloadRef,
        mode,
        apiReference: _apiReference,
        prefillVersion = 0,
        interviewPlan,
        candidateName,
        jobTitle,
      },
      ref
    ) => {
      // ---- Reducer state ----
      const [state, dispatch] = useReducer(
        interviewCommunicationReducer,
        initialInterviewCommunicationFormState,
        (base) =>
          hydrateCommunicationStateFromPayload(
            Object.keys(payloadRef.current.communication).length > 0
              ? (payloadRef.current.communication as Partial<
                  Record<string, unknown>
                >)
              : undefined,
            base,
            { stripCalendarTemplates: prefillVersion > 0 }
          )
      );

      const { candidateSentBadge, interviewerSentBadge } = useMemo(() => {
        const all =
          (interviewPlan?.sent_emails as SentEmailEntry[] | undefined) ?? [];
        const candidateEmails = all.filter((e) => e.type === 'candidate');
        const interviewerEmails = all.filter((e) => e.type === 'interviewer');
        return {
          candidateSentBadge: buildSentBadge(candidateEmails),
          interviewerSentBadge: buildSentBadge(interviewerEmails),
        };
      }, [interviewPlan]);

      const validateCommOptions = useMemo(
        () => ({
          validateSelfSchedule: isSelfScheduleMode(mode),
        }),
        [mode]
      );

      useEffect(() => {
        if (validateCommOptions.validateSelfSchedule) {
          return;
        }
        dispatch({ type: 'SET_SELF_SCHEDULE_ENABLED', payload: false });
      }, [dispatch, validateCommOptions.validateSelfSchedule]);

      const [fieldErrors, setFieldErrors] =
        useState<InterviewCommunicationValidationErrors>({});

      // ---- Data fetching ----
      // Match InterviewActivity's per-section fetches so the calendar
      // dropdown only shows calendar-category templates and the email
      // dropdown only shows email-category templates (legacy wizard did
      // the same — a single merged scope left the wrong list in each
      // dropdown and picked the wrong default template).
      const { data: candidateCalendarTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate-calendar');
      const { data: candidateEmailTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate-email');
      const { data: interviewerCalendarTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer-calendar');
      const { data: interviewerEmailTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer-email');
      const { data: selfScheduleTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('self-schedule');
      const {
        fromOptions,
        ccBccOptions,
        isFromOptionsFetched,
        isFromOptionsLoading,
      } = useScheduleSenderOptions();

      // The shared initial state seeds `from: 'organizer'` — that
      // sentinel is correct on InterviewActivity (stage-action plan),
      // but for a specific scheduled interview the From should default
      // to the user creating it. Swap each email's `from` once on mount
      // iff (a) it still holds the organizer sentinel AND (b) the
      // current user is actually delegatable. The second check closes
      // the bug where an admin-only user had their id written into
      // `from`, the Select showed empty (id not in fromOptions), and
      // the validator reported the form valid anyway.
      const currUser = useSelector(selectCurrUser) as
        | {
            id?: number;
            first_name?: string;
            last_name?: string;
            email?: string;
          }
        | null
        | undefined;
      const didSeedFromRef = useRef(false);

      // ---- Preview state (per editor block) ----
      const [candidateCalPreviewHtml, setCandidateCalPreviewHtml] = useState<
        string | undefined
      >(undefined);
      const [candidateEmailPreviewHtml, setCandidateEmailPreviewHtml] =
        useState<string | undefined>(undefined);
      const [interviewerCalPreviewHtml, setInterviewerCalPreviewHtml] =
        useState<string | undefined>(undefined);
      const [interviewerEmailPreviewHtml, setInterviewerEmailPreviewHtml] =
        useState<string | undefined>(undefined);

      const buildVars = useCallback(
        (isInterviewer = false): Record<string, string> => {
          const details = payloadRef.current.details as Record<string, unknown>;

          // Interview name
          const interviewName =
            typeof details.interviewName === 'string'
              ? details.interviewName
              : undefined;

          // Interview time — bookedSlot (manual) or first self-schedule slot
          const bookedSlot = details.bookedSlot as
            | { start?: string }
            | undefined;
          const selfScheduleSlots = details.selfScheduleSlots as
            | { start?: string }[]
            | undefined;
          const startIso =
            bookedSlot?.start ?? selfScheduleSlots?.[0]?.start ?? undefined;
          const timezone =
            typeof details.timezone === 'string' ? details.timezone : undefined;
          let interviewTime: string | undefined;
          if (startIso && timezone) {
            const m = moment.tz(startIso, timezone);
            const abbr = formatTimezoneAbbreviationForDisplay(m, timezone);
            interviewTime = `${m.format('MMM DD, YYYY, h:mm A')} ${abbr}`;
          }

          // Duration
          const durationRaw = details.duration;
          const interviewDuration =
            durationRaw !== undefined && durationRaw !== null
              ? `${durationRaw} minutes`
              : undefined;

          // Onsite location — mirrors legacy generateLocationWithMapsLink
          let interviewOnsiteLocation: string | undefined;
          if (details.location === 'onsite') {
            const office = (
              interviewPlan as Record<string, unknown> | undefined
            )?.ats_office as
              | { name?: string; location?: string; place_id?: string }
              | undefined;
            if (office?.place_id && office.location) {
              const label = isInterviewer
                ? `${office.name ?? ''}, ${office.location}`.replace(
                    /^,\s*/,
                    ''
                  )
                : office.location;
              const mapsUrl = `https://www.google.com/maps/place/?q=place_id:${office.place_id}`;
              interviewOnsiteLocation = `${label} (<a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="color:#6C63FF;text-decoration:underline;">View on maps</a>)`;
            }
          }

          // Interviewers — look up names from module-level cache
          const rawIds = details.participant_ids;
          const participantIds: string[] = (
            Array.isArray(rawIds) ? rawIds : rawIds ? [rawIds] : []
          ).map(String);
          const interviewerNames = participantIds
            .map((id) => getCachedInterviewerOption(id)?.label)
            .filter((n): n is string => Boolean(n));
          const primaryInterviewer = interviewerNames[0];
          const allInterviewers = interviewerNames.join(', ');

          // Candidate name fields
          const parts = candidateName
            ? candidateName.trim().split(/\s+/)
            : undefined;
          const firstName = parts?.[0];
          const lastName =
            parts && parts.length > 1 ? parts.slice(1).join(' ') : undefined;

          // Sender fields from currUser
          const senderFirst = currUser?.first_name;
          const senderLast = currUser?.last_name;
          const senderFull =
            senderFirst || senderLast
              ? `${senderFirst ?? ''} ${senderLast ?? ''}`.trim()
              : undefined;

          const vars: Record<string, string> = {};
          const set = (key: string, val: string | undefined) => {
            if (val !== undefined && val !== '') vars[`{{${key}}}`] = val;
          };

          set('interview.name', interviewName);
          set('interview.time', interviewTime);
          set('interview.duration', interviewDuration);
          set('interview.onsite_location', interviewOnsiteLocation);
          set('interview.interviewer_full_name', primaryInterviewer);
          set('interview.all_interviewers_full_names', allInterviewers);
          // Legacy also has first/last name variants
          set(
            'interview.interviewer_first_name',
            primaryInterviewer?.split(' ')[0]
          );
          set(
            'interview.interviewer_last_name',
            primaryInterviewer?.split(' ').slice(1).join(' ')
          );
          set('candidate.first_name', firstName);
          set('candidate.last_name', lastName);
          set('candidate.full_name', candidateName?.trim());
          set('job.title', jobTitle);
          set('sender.first_name', senderFirst);
          set('sender.last_name', senderLast);
          set('sender.full_name', senderFull);
          set('sender.email', currUser?.email);

          return vars;
        },
        [candidateName, currUser, interviewPlan, jobTitle, payloadRef]
      );
      useEffect(() => {
        if (didSeedFromRef.current) return;
        if (!isFromOptionsFetched) return;
        const fromValue = resolveDefaultFromValueForCurrentUser(
          fromOptions,
          currUser?.id
        );
        if (!fromValue) return;
        didSeedFromRef.current = true;
        if (
          state.candidateEmail.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_CANDIDATE_EMAIL',
            payload: patchEmailComposition(state.candidateEmail, {
              from: fromValue,
            }),
          });
        }
        if (
          state.interviewerEmail.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_INTERVIEWER_EMAIL',
            payload: patchEmailComposition(state.interviewerEmail, {
              from: fromValue,
            }),
          });
        }
        if (
          state.selfSchedule.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_SELF_SCHEDULE',
            payload: patchEmailComposition(state.selfSchedule, {
              from: fromValue,
            }),
          });
        }
        // Intentionally only read state.* once — this is a one-shot seed
        // keyed on the ref, not a live sync.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currUser?.id, isFromOptionsFetched, fromOptions]);

      const {
        candidateCalendar: candidateCalendarVariables,
        interviewerCalendar: interviewerCalendarVariables,
        candidateEmail: candidateEmailVariables,
        interviewerEmail: interviewerEmailVariables,
      } = useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();
      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      // ---- Validation variables ----
      const validationVariables: CommunicationValidationVariables = useMemo(
        () => ({
          candidateCalendar: candidateCalendarVariables,
          interviewerCalendar: interviewerCalendarVariables,
          candidateEmail: candidateEmailVariables,
          interviewerEmail: interviewerEmailVariables,
        }),
        [
          candidateCalendarVariables,
          candidateEmailVariables,
          interviewerCalendarVariables,
          interviewerEmailVariables,
        ]
      );

      // ---- Live validation after first submit failure ----
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!hasValidationErrors(prev)) return prev;
          return validateInterviewCommunicationForm(
            state,
            validationVariables,
            validateCommOptions
          );
        });
      }, [state, validationVariables, validateCommOptions]);

      // ---- One-shot template defaults (adhoc create only) ----
      // Adhoc flows have no interview plan to pull templates from, so
      // we fall back to the first available template per section to
      // avoid a blank editor. Manual create is intentionally excluded
      // — `useScheduleInterview` seeds `payloadRef.communication`
      // from the plan's comm fields before this step mounts, and
      // those templates are the source of truth. Edit mode is gated
      // by `prefillVersion > 0` as before.
      const isAdhoc = isAdhocMode(mode);
      const didDefaultCandidateCal = useRef(false);
      const didDefaultCandidateEmail = useRef(false);
      const didDefaultInterviewerCal = useRef(false);
      const didDefaultInterviewerEmail = useRef(false);
      const didDefaultSelfSchedule = useRef(false);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !candidateCalendarTemplateOptions.length) {
          return;
        }
        if (state.candidateCalendar.template) {
          didDefaultCandidateCal.current = true;
          return;
        }
        if (didDefaultCandidateCal.current) return;
        didDefaultCandidateCal.current = true;
        dispatch({
          type: 'SET_CANDIDATE_CALENDAR',
          payload: compositionFromTemplateOption(
            state.candidateCalendar,
            candidateCalendarTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        candidateCalendarTemplateOptions,
        state.candidateCalendar,
      ]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !candidateEmailTemplateOptions.length) {
          return;
        }
        if (state.candidateEmail.template) {
          didDefaultCandidateEmail.current = true;
          return;
        }
        if (didDefaultCandidateEmail.current) return;
        didDefaultCandidateEmail.current = true;
        dispatch({
          type: 'SET_CANDIDATE_EMAIL',
          payload: compositionFromTemplateOption(
            state.candidateEmail,
            candidateEmailTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        candidateEmailTemplateOptions,
        state.candidateEmail,
      ]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !interviewerCalendarTemplateOptions.length) {
          return;
        }
        if (state.interviewerCalendar.template) {
          didDefaultInterviewerCal.current = true;
          return;
        }
        if (didDefaultInterviewerCal.current) return;
        didDefaultInterviewerCal.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_CALENDAR',
          payload: compositionFromTemplateOption(
            state.interviewerCalendar,
            interviewerCalendarTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        interviewerCalendarTemplateOptions,
        state.interviewerCalendar,
      ]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !interviewerEmailTemplateOptions.length) {
          return;
        }
        if (state.interviewerEmail.template) {
          didDefaultInterviewerEmail.current = true;
          return;
        }
        if (didDefaultInterviewerEmail.current) return;
        didDefaultInterviewerEmail.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_EMAIL',
          payload: compositionFromTemplateOption(
            state.interviewerEmail,
            interviewerEmailTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        interviewerEmailTemplateOptions,
        state.interviewerEmail,
      ]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !selfScheduleTemplateOptions.length) return;
        if (!validateCommOptions.validateSelfSchedule) return;
        if (state.selfSchedule.template) {
          didDefaultSelfSchedule.current = true;
          return;
        }
        if (didDefaultSelfSchedule.current) return;
        didDefaultSelfSchedule.current = true;
        dispatch({
          type: 'SET_SELF_SCHEDULE',
          payload: compositionFromTemplateOption(
            state.selfSchedule,
            selfScheduleTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        selfScheduleTemplateOptions,
        state.selfSchedule,
        validateCommOptions.validateSelfSchedule,
      ]);

      // ---- Imperative handle ----
      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateInterviewCommunicationForm(
              state,
              validationVariables,
              validateCommOptions
            );
            setFieldErrors(errors);
            const ok = !hasValidationErrors(errors);
            if (!ok) {
              const target = getFirstScheduleCommunicationScrollTarget(errors);
              if (target) {
                scrollScheduleCommunicationSectionIntoView(target);
              }
            }
            return ok;
          },
          syncToPayload: () => {
            const comm = toInterviewCommunicationPayload(state);
            comm._ui_calendar_candidate_name = calendarInviteTemplateNameForApi(
              state.candidateCalendar,
              candidateCalendarTemplateOptions
            );
            comm._ui_calendar_interviewer_name =
              calendarInviteTemplateNameForApi(
                state.interviewerCalendar,
                interviewerCalendarTemplateOptions
              );
            payloadRef.current.communication = comm as unknown as Record<
              string,
              unknown
            >;
          },
        }),
        [
          candidateCalendarTemplateOptions,
          interviewerCalendarTemplateOptions,
          payloadRef,
          state,
          validationVariables,
          validateCommOptions,
        ]
      );

      // ---- Render ----
      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          gap="6"
          overflow="auto"
          pb="8"
        >
          <CandidatePane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            calendarTemplateOptions={candidateCalendarTemplateOptions}
            emailTemplateOptions={candidateEmailTemplateOptions}
            fromOptions={fromOptions}
            isFromOptionsLoading={isFromOptionsLoading}
            ccBccOptions={ccBccOptions}
            calendarVariables={candidateCalendarVariables}
            emailVariables={candidateEmailVariables}
            uploadInlineImage={uploadInlineImage}
            sentEmailBadge={candidateSentBadge}
            calendarPreviewProps={{
              previewHtml: candidateCalPreviewHtml,
              onPreviewClick: () =>
                setCandidateCalPreviewHtml(
                  buildPreviewHtml(state.candidateCalendar.content, buildVars())
                ),
              onPreviewDismiss: () => setCandidateCalPreviewHtml(undefined),
            }}
            emailPreviewProps={{
              previewHtml: candidateEmailPreviewHtml,
              onPreviewClick: () =>
                setCandidateEmailPreviewHtml(
                  buildPreviewHtml(state.candidateEmail.content, buildVars())
                ),
              onPreviewDismiss: () => setCandidateEmailPreviewHtml(undefined),
            }}
          />
          <InterviewerPane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            calendarTemplateOptions={interviewerCalendarTemplateOptions}
            emailTemplateOptions={interviewerEmailTemplateOptions}
            fromOptions={fromOptions}
            isFromOptionsLoading={isFromOptionsLoading}
            ccBccOptions={ccBccOptions}
            calendarVariables={interviewerCalendarVariables}
            emailVariables={interviewerEmailVariables}
            uploadInlineImage={uploadInlineImage}
            sentEmailBadge={interviewerSentBadge}
            calendarPreviewProps={{
              previewHtml: interviewerCalPreviewHtml,
              onPreviewClick: () =>
                setInterviewerCalPreviewHtml(
                  buildPreviewHtml(
                    state.interviewerCalendar.content,
                    buildVars(true)
                  )
                ),
              onPreviewDismiss: () => setInterviewerCalPreviewHtml(undefined),
            }}
            emailPreviewProps={{
              previewHtml: interviewerEmailPreviewHtml,
              onPreviewClick: () =>
                setInterviewerEmailPreviewHtml(
                  buildPreviewHtml(
                    state.interviewerEmail.content,
                    buildVars(true)
                  )
                ),
              onPreviewDismiss: () => setInterviewerEmailPreviewHtml(undefined),
            }}
          />
        </Flex>
      );
    }
  )
);

CommunicationStep.displayName = 'CommunicationStep';
